import {
  CSSProperties,
  RefCallback,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUpdateEffect } from 'react-use';

import { SplitPositionBounds, UseSplitterOptions } from './useSplitter.types';

type UseSplitterResult = {
  /** The styles to be applied on the container element. */
  readonly containerStyles: CSSProperties;
  /** Whether the panels are resizing. */
  readonly isResizing: boolean;
  /** The ref function for the container element. */
  readonly containerRefFn: RefCallback<HTMLElement>;
  /** The ref function for the splitter element. */
  readonly splitterRefFn: RefCallback<HTMLElement>;
};

/**
 * A hook for handling splitting and resizing an element with 2 panels.
 *
 * Because the hook only implements the logic and returns minimal computed
 * styles, it is up to the consumer to scaffold the elements, i.e. container,
 * panels, splitter, and supply them with necessary
 * {@link containerStyles styles} and ref functions
 * ({@link UseSplitterResult.containerRefFn container} and
 * {@link UseSplitterResult.splitterRefFn splitter}) returned by the hook.
 *
 * The hook relies on CSS grid to split the container in two panels. The resize
 * handle **must** be placed inside the end panel by the consumer. Normally the
 * handle would be placed close to the edge between the start and end panels,
 * but it could technically be placed anywhere inside the end panel.
 *
 * @example
 *   const { containerRefFn, splitterRefFn, containerStyles } = useSplitter({
 *     orientation = 'horizontal',
 *     startPanelMinSize: 100,
 *     endPanelMinSize: 100,
 *   });
 *
 *   return (
 *     <div ref={containerRefFn} style={{ ...containerStyles, height: 500 }}>
 *       <div>Start panel</div>
 *       <div>
 *         <div ref={splitterRefFn} className={styles.splitter} />
 *         End panel
 *       </div>
 *     </div>
 *   );
 */
export const useSplitter = ({
  orientation,
  endPanelMinSize = 0,
  startPanelMinSize = 0,
  splitPosition: initialSplitPosition = 50,
  onResize,
  onResizeEnd,
  onDoubleClick,
}: UseSplitterOptions): UseSplitterResult => {
  const [isStarted, setIsStarted] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [containerRect, setContainerRect] = useState<DOMRect>();
  const [startOffset, setStartOffset] = useState(0);
  const [splitPosition, setSplitPosition] = useState(initialSplitPosition);
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(
    null,
  );
  const [splitterElement, setSplitterElement] = useState<HTMLElement | null>(
    null,
  );
  const lastResizeEndRef = useRef(0);

  /** Whether the split orientation is vertical. */
  const isVerticalSplit = orientation === 'vertical';

  // Sync internal state when the prop changes externally (e.g. double-click reset).
  useEffect(() => {
    setSplitPosition(initialSplitPosition);
  }, [initialSplitPosition]);

  /** The bounds in which the splitter can operate. */
  const splitPositionBounds: SplitPositionBounds = useMemo(() => {
    if (!containerRect) {
      return { min: 0, max: 100 };
    }

    const containerSize = containerRect[isVerticalSplit ? 'width' : 'height'];

    return {
      min: (startPanelMinSize * 100) / containerSize,
      max: 100 - (endPanelMinSize * 100) / containerSize,
    };
  }, [containerRect, endPanelMinSize, isVerticalSplit, startPanelMinSize]);

  /** The styles to be applied on the container element by the consumer. */
  const containerStyles = useMemo<CSSProperties>(() => {
    const gridTemplateValue = `minmax(${startPanelMinSize}px, ${splitPosition}%) minmax(${endPanelMinSize}px, 1fr)`;

    return {
      display: 'grid',
      ...(isVerticalSplit
        ? {
            gridTemplateColumns: gridTemplateValue,
          }
        : {
            gridTemplateRows: gridTemplateValue,
          }),
    };
  }, [endPanelMinSize, isVerticalSplit, splitPosition, startPanelMinSize]);

  /** Handles the start of the resize of the panels. */
  const resizeStart = useCallback(
    (event: MouseEvent) => {
      if (!(splitterElement && containerElement && !isStarted)) {
        return;
      }

      setIsStarted(true);
      setContainerRect(containerElement.getBoundingClientRect());

      // Gets computed transform styles to include in the final offset
      // calculation. This is important as the consumer might choose to offset
      // using `transform` CSS property to position the splitter element.
      const { e: transformX, f: transformY } = new DOMMatrix(
        window.getComputedStyle(splitterElement).transform,
      );

      const mousePosition = event[isVerticalSplit ? 'clientX' : 'clientY'];
      const splitterPosition =
        splitterElement.getBoundingClientRect()[isVerticalSplit ? 'x' : 'y'];
      const splitterOffset =
        splitterElement[isVerticalSplit ? 'offsetLeft' : 'offsetTop'];
      const splitterTransform = isVerticalSplit ? transformX : transformY;

      setStartOffset(
        mousePosition - (splitterPosition - splitterOffset - splitterTransform),
      );
    },
    [containerElement, isStarted, isVerticalSplit, splitterElement],
  );

  /** Handles the resize of the panels. */
  const resize = useCallback(
    (event: MouseEvent) => {
      if (!(containerRect && isStarted)) {
        return;
      }

      setIsResizing(true);

      setSplitPosition(
        Math.min(
          Math.max(
            ((event[isVerticalSplit ? 'clientX' : 'clientY'] -
              containerRect[isVerticalSplit ? 'x' : 'y'] -
              startOffset) *
              100) /
              containerRect[isVerticalSplit ? 'width' : 'height'],
            splitPositionBounds.min,
          ),
          splitPositionBounds.max,
        ),
      );
    },
    [
      containerRect,
      isStarted,
      isVerticalSplit,
      splitPositionBounds.max,
      splitPositionBounds.min,
      startOffset,
    ],
  );

  /** Handles the end of the resize of the panels. */
  const resizeEnd = useCallback(() => {
    if (isStarted) {
      setIsStarted(false);
      setIsResizing(false);

      const now = Date.now();
      if (now - lastResizeEndRef.current < 300) {
        onDoubleClick?.();
      } else {
        onResizeEnd?.(splitPosition);
      }
      lastResizeEndRef.current = now;
    }
  }, [isStarted, splitPosition, onResizeEnd, onDoubleClick]);

  // Handles the resize event callback when resizing. Note that we choose to do
  // that inside an effect rather than the resize function as we only want to
  // emit when split position changes.
  useUpdateEffect(() => {
    if (isResizing) {
      onResize?.(splitPosition);
    }
  }, [isResizing, splitPosition, onResizeEnd]);

  // Handles event listeners.
  useEffect(() => {
    if (splitterElement && containerElement) {
      splitterElement.addEventListener('mousedown', resizeStart);
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', resizeEnd);
    }

    return () => {
      if (splitterElement && containerElement) {
        splitterElement.removeEventListener('mousedown', resizeStart);
        window.removeEventListener('mousemove', resize);
        window.removeEventListener('mouseup', resizeEnd);
      }
    };
  }, [containerElement, splitterElement, resize, resizeEnd, resizeStart]);

  return {
    containerStyles,
    isResizing,
    containerRefFn: setContainerElement,
    splitterRefFn: setSplitterElement,
  };
};
